/**
 * Invoice Turnstile Component
 * Cloudflare Turnstile widget for bot protection
 */

import { useState, useEffect, useRef } from 'react';

interface Props {
  onVerify: (token: string) => void;
  onError: () => void;
  siteKey?: string;
}

// Global flag to prevent multiple script loads
let turnstileScriptLoaded = false;

export function InvoiceTurnstile({ onVerify, onError, siteKey }: Props) {
  const [turnstileStatus, setTurnstileStatus] = useState<'loading' | 'success' | 'error' | 'expired'>('loading');
  const [isLoaded, setIsLoaded] = useState(false);
  const [widgetRendered, setWidgetRendered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Store callbacks in refs to prevent re-renders
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onVerifyRef.current = onVerify;
    onErrorRef.current = onError;
  });

  // Use provided siteKey or fall back to environment variable
  const effectiveSiteKey = siteKey || import.meta.env?.VITE_TURNSTILE_SITE_KEY || '';

  // Load Turnstile script dynamically (only once globally)
  useEffect(() => {
    const siteKeyString = String(effectiveSiteKey || '').trim();

    if (!siteKeyString || siteKeyString === '' || siteKeyString === 'undefined' || turnstileScriptLoaded) {
      if (turnstileScriptLoaded) {
        setIsLoaded(true);
      }
      return;
    }

    // Check if script already exists
    const existingScript = document.querySelector('script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]');
    if (existingScript) {
      turnstileScriptLoaded = true;
      setIsLoaded(true);
      return;
    }

    // Load Turnstile script
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.onload = () => {
      turnstileScriptLoaded = true;
      setIsLoaded(true);
    };
    script.onerror = () => {
      setTurnstileStatus('error');
      onError();
    };
    document.head.appendChild(script);

    return () => {
      // Don't remove the script on unmount as it may be used by other components
    };
  }, [effectiveSiteKey, onError]);

  // If no site key is configured, don't render the widget
  if (!effectiveSiteKey) {
    return null;
  }

  // Render Turnstile widget when script is loaded (only once)
  useEffect(() => {
    const siteKeyString = String(effectiveSiteKey || '').trim();

    if (!isLoaded || !containerRef.current || widgetRendered || !siteKeyString || siteKeyString === 'undefined') return;

    // Clear any existing widget
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    // @ts-ignore - Turnstile global
    if (typeof window.turnstile !== 'undefined') {
      // @ts-ignore - Turnstile global
      const widgetId = window.turnstile.render(containerRef.current, {
        sitekey: siteKeyString,
        callback: (token: string) => {
          setTurnstileStatus('success');
          onVerifyRef.current(token);
        },
        'error-callback': () => {
          setTurnstileStatus('error');
          onErrorRef.current();
        },
        'expired-callback': () => {
          setTurnstileStatus('expired');
        },
        theme: 'light',
      });
      widgetIdRef.current = widgetId;
      setWidgetRendered(true);
    }

    // Cleanup on unmount
    return () => {
      if (widgetIdRef.current) {
        // @ts-ignore - Turnstile global
        if (typeof window.turnstile !== 'undefined') {
          // @ts-ignore - Turnstile global
          window.turnstile.remove(widgetIdRef.current);
        }
      }
      setWidgetRendered(false);
    };
  }, [isLoaded, effectiveSiteKey]); // Remove onVerify and onError from dependencies

  return (
    <div className="turnstile-container my-4">
      <div ref={containerRef} className="cf-turnstile"></div>

      {/* Status messages */}
      {!isLoaded && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Loading verification...
        </p>
      )}

      {turnstileStatus === 'error' && (
        <p className="text-sm text-red-600 dark:text-red-400 mt-2">
          Verification failed. Please try again.
        </p>
      )}

      {turnstileStatus === 'expired' && (
        <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
          Verification expired. Please refresh.
        </p>
      )}
    </div>
  );
}
